package one.healthium.onboarding.ui.components

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.spring
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.RectangleShape
import androidx.compose.ui.layout.SubcomposeLayout
import androidx.compose.ui.layout.layout
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay


@Composable
fun AnimatableCalendarWheel(
    modifier: Modifier = Modifier,
    datesList: List<String>,
    height: Dp,
    style: TextStyle,
) {
    if (datesList.isEmpty()) return

    Box(
        modifier = modifier.wrapContentHeight(unbounded = true),
    ) {
        Box(
            Modifier
                .clip(RectangleShape)
                .layout { measurable, constraints ->
                    val placeable = measurable.measure(constraints)
                    layout(width = placeable.width, height = height.roundToPx()) {
                        placeable.place(0, 0)
                    }
                }
        ) {
            AnimatableWheel(
                values = datesList,
                height = height.value.toInt(),
                style = style,
            )
        }
    }
}

@Composable
private fun AnimatableWheel(
    values: List<String>,
    height: Int,
    style: TextStyle,
) {
    fun offset(value: String): Float {
        val offsetMultiplier = values.indexOf(value).toFloat()
        return -height * offsetMultiplier
    }

    val itemTarget = remember { Animatable(offset(values.first())) }

    LaunchedEffect(key1 = values) {
        delay(500)
        itemTarget.animateTo(
            targetValue = offset(values.last()),
            animationSpec = spring(
                dampingRatio = 1f,
                stiffness = 4.5f,
                visibilityThreshold = 1f, // TODO
            ),
        )
    }

    Column(
        modifier = Modifier.offset(y = itemTarget.value.dp),
    ) {
        repeat(values.count()) {
            AnimatableItem(
                modifier = Modifier.height(height.dp),
                value = values[it],
                style = style,
            )
        }
    }
}

@Composable
fun AnimatableItem(
    modifier: Modifier = Modifier,
    value: String,
    style: TextStyle,
) {
    Text(
        modifier = modifier,
        text = value,
        style = style,
    )
}

@Composable
fun MeasureUnconstrainedViewWidth(
    viewToMeasure: @Composable () -> Unit,
    content: @Composable (measuredWidth: Dp, measuredHeight: Dp) -> Unit,
) {
    SubcomposeLayout { constraints ->
        val measure = subcompose("viewToMeasureWidth", viewToMeasure)[0].measure(Constraints())
        val measuredWidth = measure.width.toDp()
        val measuredHeight = measure.height.toDp()
        val contentPlaceable = subcompose("content") { content(measuredWidth, measuredHeight) }[0].measure(constraints)

        layout(contentPlaceable.width, contentPlaceable.height) {
            contentPlaceable.place(0, 0)
        }
    }
}
